import { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";

import { useApi } from "../hooks/useApi";
import dayjs from "dayjs";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const REASON_LABELS = {
  lunch: "🍽 Lunch break",
  break: "☕ Break",
  vacation: "🏖 Vacation",
  sick_day: "🤒 Sick day",
  personal: "Personal",
  other: "Other",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 },
};

// ── Inline section header ─────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      color: "text.secondary",
      mb: 1.5,
      letterSpacing: 0.8,
    }}
  >
    {children}
  </Typography>
);

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyRow = ({ text }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 1.5,
      border: "1px solid",
      borderColor: "customDivider",
      backgroundColor: "offWhite",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {text}
    </Typography>
  </Box>
);

// ─── Main dialog ──────────────────────────────────────────────────────────────
const BarberExceptionsDialog = ({ open, onClose, barber }) => {
  const api = useApi();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [overrides, setOverrides] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editing state for overrides
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Editing state for blocks
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editBlockForm, setEditBlockForm] = useState({});

  const [saving, setSaving] = useState(false);

  // Deleting state
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    if (!barber?.user_id) return;
    setLoading(true);
    setError(null);
    try {
      const [ovRes, blRes] = await Promise.all([
        api(`/barbers/date-overrides.php?barber_id=${barber.user_id}`, {
          method: "GET",
        }),
        api(`/barbers/blocks.php?barber_id=${barber.user_id}`, {
          method: "GET",
        }),
      ]);
      // Sort overrides by date ascending, filter to today and future
      const today = dayjs().format("YYYY-MM-DD");
      setOverrides(
        (ovRes.data.overrides || [])
          .filter((o) => o.date >= today)
          .sort((a, b) => a.date.localeCompare(b.date)),
      );
      setBlocks(blRes.data.blocks || []);
    } catch (err) {
      setError("Failed to load exceptions.");
    } finally {
      setLoading(false);
    }
  }, [barber?.user_id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // ── Override: start editing ───────────────────────────────────────────────
  const startEdit = (override) => {
    setEditingId(override.id);
    setEditForm({
      date: override.date,
      start_time: override.start_time.slice(0, 5), // HH:MM
      end_time: override.end_time.slice(0, 5),
      notes: override.notes || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (overrideId) => {
    setSaving(true);
    try {
      await api("/barbers/date-overrides.php", {
        method: "PUT",
        body: JSON.stringify({
          override_id: overrideId,
          date: editForm.date,
          start_time: `${editForm.start_time}:00`,
          end_time: `${editForm.end_time}:00`,
          notes: editForm.notes || null,
        }),
      });
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err?.message || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  // ── Override: delete ──────────────────────────────────────────────────────
  const deleteOverride = async (overrideId) => {
    setDeletingId(overrideId);
    try {
      await api("/barbers/date-overrides.php", {
        method: "DELETE",
        body: JSON.stringify({ override_id: overrideId }),
      });
      await load();
    } catch (err) {
      setError(err?.message || "Failed to remove.");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Block: delete ─────────────────────────────────────────────────────────
  const deleteBlock = async (blockId) => {
    setDeletingId(blockId);
    try {
      await api("/barbers/blocks.php", {
        method: "DELETE",
        body: JSON.stringify({ block_id: blockId }),
      });
      await load();
    } catch (err) {
      setError(err?.message || "Failed to remove.");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Block: start / save / cancel editing ─────────────────────────────────
  const startEditBlock = (block) => {
    setEditingBlockId(block.id);
    setEditBlockForm({
      reason: block.reason,
      notes: block.notes || "",
      // one-time
      date: block.starts_at ? block.starts_at.slice(0, 10) : "",
      start_time_one: block.starts_at
        ? dayjs(block.starts_at.replace(" ", "T") + "Z").format("HH:mm")
        : "",
      end_time_one: block.ends_at
        ? dayjs(block.ends_at.replace(" ", "T") + "Z").format("HH:mm")
        : "",
      // recurring
      day_of_week:
        block.day_of_week !== null ? String(block.day_of_week) : "all",
      start_time_rec: block.start_time ? block.start_time.slice(0, 5) : "",
      end_time_rec: block.end_time ? block.end_time.slice(0, 5) : "",
      recurring_start_date: block.recurring_start_date || "",
      recurring_end_date: block.recurring_end_date || "",
    });
  };

  const cancelEditBlock = () => {
    setEditingBlockId(null);
    setEditBlockForm({});
  };

  const saveEditBlock = async (block) => {
    setSaving(true);
    try {
      const body = {
        block_id: block.id,
        reason: editBlockForm.reason,
        notes: editBlockForm.notes || null,
      };
      if (!block.is_recurring) {
        body.starts_at = dayjs(
          `${editBlockForm.date}T${editBlockForm.start_time_one}:00`,
        )
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
        body.ends_at = dayjs(
          `${editBlockForm.date}T${editBlockForm.end_time_one}:00`,
        )
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      } else {
        body.day_of_week =
          editBlockForm.day_of_week === "all"
            ? null
            : Number(editBlockForm.day_of_week);
        body.start_time = `${editBlockForm.start_time_rec}:00`;
        body.end_time = `${editBlockForm.end_time_rec}:00`;
        body.recurring_start_date = editBlockForm.recurring_start_date || null;
        body.recurring_end_date = editBlockForm.recurring_end_date || null;
      }
      await api("/barbers/blocks.php", {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setEditingBlockId(null);
      await load();
    } catch (err) {
      setError(err?.message || "Failed to update block.");
    } finally {
      setSaving(false);
    }
  };

  // ── Block schedule description ────────────────────────────────────────────
  const blockScheduleLabel = (block) => {
    if (!block.is_recurring) {
      const d = dayjs(block.starts_at.replace(" ", "T") + "Z");
      return `${d.format("ddd, MMM D, YYYY")} · ${d.format("h:mm A")} – ${dayjs(block.ends_at.replace(" ", "T") + "Z").format("h:mm A")}`;
    }
    const day =
      block.day_of_week !== null ? DAY_NAMES[block.day_of_week] : "Every day";
    const start = block.start_time
      ? dayjs(`2000-01-01 ${block.start_time}`).format("h:mm A")
      : "";
    const end = block.end_time
      ? dayjs(`2000-01-01 ${block.end_time}`).format("h:mm A")
      : "";
    const range = block.recurring_start_date
      ? ` · from ${block.recurring_start_date}${block.recurring_end_date ? ` to ${block.recurring_end_date}` : ""}`
      : "";
    return `${day} · ${start} – ${end}${range}`;
  };

  // ── Row ───────────────────────────────────────────────────────────────────
  const rowSx = {
    p: 1.5,
    borderRadius: 1.5,
    border: "1px solid",
    borderColor: "customDivider",
    backgroundColor: "offWhite",
    display: "flex",
    flexDirection: "column",
    gap: 1,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Manage Exceptions
          </Typography>
          {barber && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {barber.first_name} {barber.last_name}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ "&&": { pt: 3 } }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 1.5, fontSize: 13 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 4,
              justifyContent: "center",
            }}
          >
            <CircularProgress size={18} sx={{ color: "text.secondary" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <>
            {/* ── Added Availability ──────────────────────────────── */}
            <SectionLabel>Added Availability</SectionLabel>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2, fontSize: 12 }}
            >
              Dates the barber is available outside their regular schedule.
            </Typography>

            {overrides.length === 0 ? (
              <EmptyRow text="No availability exceptions added." />
            ) : (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}
              >
                {overrides.map((ov) =>
                  editingId === ov.id ? (
                    // ── Edit mode ───────────────────────────────────
                    <Box key={ov.id} sx={rowSx}>
                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <TextField
                          label="Date"
                          type="date"
                          size="small"
                          sx={{ ...INPUT_SX, flex: 1 }}
                          value={editForm.date}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, date: e.target.value }))
                          }
                          slotProps={{
                            htmlInput: { min: dayjs().format("YYYY-MM-DD") },
                          }}
                        />
                        <TextField
                          label="Start"
                          type="time"
                          size="small"
                          sx={{ ...INPUT_SX, width: 120 }}
                          value={editForm.start_time}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              start_time: e.target.value,
                            }))
                          }
                        />
                        <TextField
                          label="End"
                          type="time"
                          size="small"
                          sx={{ ...INPUT_SX, width: 120 }}
                          value={editForm.end_time}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              end_time: e.target.value,
                            }))
                          }
                        />
                      </Box>
                      <TextField
                        label="Notes (optional)"
                        size="small"
                        fullWidth
                        sx={INPUT_SX}
                        value={editForm.notes}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, notes: e.target.value }))
                        }
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          size="small"
                          onClick={cancelEdit}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            saving ? (
                              <CircularProgress size={12} color="inherit" />
                            ) : (
                              <CheckIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          disabled={saving}
                          onClick={() => saveEdit(ov.id)}
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#1a1a1a",
                            "&:hover": { backgroundColor: "#000" },
                          }}
                        >
                          Save
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    // ── View mode ───────────────────────────────────
                    <Box
                      key={ov.id}
                      sx={{
                        ...rowSx,
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {dayjs(ov.date).format("ddd, MMM D, YYYY")}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {dayjs(`2000-01-01 ${ov.start_time}`).format(
                            "h:mm A",
                          )}{" "}
                          –{" "}
                          {dayjs(`2000-01-01 ${ov.end_time}`).format("h:mm A")}
                          {ov.notes ? ` · ${ov.notes}` : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => startEdit(ov)}
                          sx={{ color: "text.secondary" }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteOverride(ov.id)}
                          disabled={deletingId === ov.id}
                          sx={{ color: "error.main" }}
                        >
                          {deletingId === ov.id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            )}

            <Divider sx={{ my: 2.5 }} />

            {/* ── Time Blocks ──────────────────────────────────────── */}
            <SectionLabel>Time Blocks</SectionLabel>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2, fontSize: 12 }}
            >
              Periods when the barber is unavailable for bookings.
            </Typography>

            {blocks.length === 0 ? (
              <EmptyRow text="No time blocks added." />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {blocks.map((block) =>
                  editingBlockId === block.id ? (
                    // ── Block edit mode ─────────────────────────────
                    <Box key={block.id} sx={rowSx}>
                      {/* Reason */}
                      <Select
                        size="small"
                        value={editBlockForm.reason}
                        onChange={(e) =>
                          setEditBlockForm((f) => ({
                            ...f,
                            reason: e.target.value,
                          }))
                        }
                        sx={{ fontSize: 13, borderRadius: 1.5 }}
                      >
                        {Object.entries(REASON_LABELS).map(([val, label]) => (
                          <MenuItem key={val} value={val}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>

                      {!block.is_recurring ? (
                        // One-time: date + times
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <TextField
                            label="Date"
                            type="date"
                            size="small"
                            sx={{ ...INPUT_SX, flex: 1 }}
                            value={editBlockForm.date}
                            onChange={(e) =>
                              setEditBlockForm((f) => ({
                                ...f,
                                date: e.target.value,
                              }))
                            }
                          />
                          <TextField
                            label="Start"
                            type="time"
                            size="small"
                            sx={{ ...INPUT_SX, width: 110 }}
                            value={editBlockForm.start_time_one}
                            onChange={(e) =>
                              setEditBlockForm((f) => ({
                                ...f,
                                start_time_one: e.target.value,
                              }))
                            }
                          />
                          <TextField
                            label="End"
                            type="time"
                            size="small"
                            sx={{ ...INPUT_SX, width: 110 }}
                            value={editBlockForm.end_time_one}
                            onChange={(e) =>
                              setEditBlockForm((f) => ({
                                ...f,
                                end_time_one: e.target.value,
                              }))
                            }
                          />
                        </Box>
                      ) : (
                        // Recurring: day + times + optional date range
                        <>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Select
                              size="small"
                              value={editBlockForm.day_of_week}
                              onChange={(e) =>
                                setEditBlockForm((f) => ({
                                  ...f,
                                  day_of_week: e.target.value,
                                }))
                              }
                              sx={{ fontSize: 13, borderRadius: 1.5, flex: 1 }}
                            >
                              <MenuItem value="all">Every day</MenuItem>
                              {DAY_NAMES.map((d, i) => (
                                <MenuItem key={i} value={String(i)}>
                                  {d}
                                </MenuItem>
                              ))}
                            </Select>
                            <TextField
                              label="Start"
                              type="time"
                              size="small"
                              sx={{ ...INPUT_SX, width: 110 }}
                              value={editBlockForm.start_time_rec}
                              onChange={(e) =>
                                setEditBlockForm((f) => ({
                                  ...f,
                                  start_time_rec: e.target.value,
                                }))
                              }
                            />
                            <TextField
                              label="End"
                              type="time"
                              size="small"
                              sx={{ ...INPUT_SX, width: 110 }}
                              value={editBlockForm.end_time_rec}
                              onChange={(e) =>
                                setEditBlockForm((f) => ({
                                  ...f,
                                  end_time_rec: e.target.value,
                                }))
                              }
                            />
                          </Box>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <TextField
                              label="From (optional)"
                              type="date"
                              size="small"
                              sx={{ ...INPUT_SX, flex: 1 }}
                              value={editBlockForm.recurring_start_date}
                              onChange={(e) =>
                                setEditBlockForm((f) => ({
                                  ...f,
                                  recurring_start_date: e.target.value,
                                }))
                              }
                            />
                            <TextField
                              label="Until (optional)"
                              type="date"
                              size="small"
                              sx={{ ...INPUT_SX, flex: 1 }}
                              value={editBlockForm.recurring_end_date}
                              onChange={(e) =>
                                setEditBlockForm((f) => ({
                                  ...f,
                                  recurring_end_date: e.target.value,
                                }))
                              }
                            />
                          </Box>
                        </>
                      )}

                      <TextField
                        label="Notes (optional)"
                        size="small"
                        fullWidth
                        sx={INPUT_SX}
                        value={editBlockForm.notes}
                        onChange={(e) =>
                          setEditBlockForm((f) => ({
                            ...f,
                            notes: e.target.value,
                          }))
                        }
                      />

                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          size="small"
                          onClick={cancelEditBlock}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            saving ? (
                              <CircularProgress size={12} color="inherit" />
                            ) : (
                              <CheckIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          disabled={saving}
                          onClick={() => saveEditBlock(block)}
                          sx={{
                            textTransform: "none",
                            backgroundColor: "#1a1a1a",
                            "&:hover": { backgroundColor: "#000" },
                          }}
                        >
                          Save
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    // ── Block view mode ─────────────────────────────
                    <Box
                      key={block.id}
                      sx={{
                        ...rowSx,
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {REASON_LABELS[block.reason] || "Blocked"}
                          </Typography>
                          {block.is_recurring && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              🔁 Recurring
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {blockScheduleLabel(block)}
                        </Typography>
                        {block.notes && (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", display: "block" }}
                          >
                            {block.notes}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => startEditBlock(block)}
                          sx={{ color: "text.secondary" }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteBlock(block.id)}
                          disabled={deletingId === block.id}
                          sx={{ color: "error.main" }}
                        >
                          {deletingId === block.id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BarberExceptionsDialog;
